<template>
  <Modal :active="active" @close="emits('close')">
    <template #header>
      {{ $t("customFlows.duplicateModal.title") }}
    </template>

    <template #body>
      {{ $t("customFlows.duplicateModal.message") }}

      <input
        type="text"
        ref="titleInput"
        v-model="newTitle"
        class="input"
        :placeholder="$t('customFlows.duplicateModal.placeholder')"
        @keydown.enter="onTitleEnter"
      />
    </template>

    <template #footer>
      <div class="buttons">
        <button
          class="button primary"
          @click="duplicateFlow"
          :disabled="emptyName || duplicating"
        >
          {{ $t("customFlows.duplicateModal.submit") }}
        </button>
        <button class="button" @click="emits('close')">
          {{ $t("cancel") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { Modal } from "@/components";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useCopyCustomFlowMutation } from "@/graphql/operations";

const props = defineProps({
  active: {
    type: Boolean,
    required: true,
  },
  flowId: {
    type: String,
    required: true,
  },
  flowTitle: {
    type: String,
    required: true,
  },
});
const emits = defineEmits(["close", "success", "error"]);

const { t } = useI18n({});

const titleInput = ref<HTMLInputElement>();
onMounted(() => titleInput.value?.focus());

const newTitle = ref<string>(
  t("customFlows.copyTitle", { title: props.flowTitle }),
);
const emptyName = computed(() => newTitle.value.trim().length === 0);
const duplicating = ref<boolean>(false);

const { executeMutation: executeCopy } = useCopyCustomFlowMutation();
const duplicateFlow = async () => {
  if (emptyName.value || duplicating.value) {
    return;
  }

  duplicating.value = true;
  const res = await executeCopy({
    id: props.flowId,
    newTitle: newTitle.value.trim(),
  });
  duplicating.value = false;

  const newFlowId = res.data?.copyFlow?.copiedFlow?.id;
  if (res.error || !newFlowId) {
    emits("error");
  } else {
    emits("success", newFlowId);
  }
};
const onTitleEnter = (event: KeyboardEvent) => {
  if (event.isComposing || event.repeat) return;
  event.preventDefault();
  void duplicateFlow();
};
</script>

<style scoped lang="scss">
.input {
  margin-top: 1em;
}
</style>
